import React from 'react';
import { getHeadingClass, combineTypography } from '../../utils/typography';

/**
 * Responsive Heading Component
 * 
 * Automatically scales text size across breakpoints
 * Industry Pattern: GitHub, Linear, Stripe
 * 
 * Features:
 * - Semantic HTML (h1-h6)
 * - Responsive text scaling
 * - Custom className support
 * - TypeScript strict typing
 * 
 * @example
 * ```tsx
 * <ResponsiveHeading level={1}>Dashboard</ResponsiveHeading>
 * <ResponsiveHeading level={2} className="text-blue-600">Reports</ResponsiveHeading>
 * ```
 */

export interface ResponsiveHeadingProps {
  /** Heading level (1-6) maps to h1-h6 */
  level: 1 | 2 | 3 | 4 | 5 | 6;
  
  /** Content to render */
  children: React.ReactNode;
  
  /** Additional CSS classes */
  className?: string;
  
  /** HTML id attribute */
  id?: string;
  
  /** ARIA label for accessibility */
  ariaLabel?: string;
}

export const ResponsiveHeading: React.FC<ResponsiveHeadingProps> = ({
  level,
  children,
  className = '',
  id,
  ariaLabel,
}) => {
  const baseClass = getHeadingClass(level);
  const combinedClass = combineTypography(
    baseClass,
    'text-gray-900 dark:text-white', // Default colors
    className
  );

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <Tag
      id={id}
      className={combinedClass}
      aria-label={ariaLabel}
    >
      {children}
    </Tag>
  );
};

/**
 * Convenience components for each heading level
 * Provides better DX with autocomplete
 */
export const H1: React.FC<Omit<ResponsiveHeadingProps, 'level'>> = (props) => (
  <ResponsiveHeading level={1} {...props} />
);

export const H2: React.FC<Omit<ResponsiveHeadingProps, 'level'>> = (props) => (
  <ResponsiveHeading level={2} {...props} />
);

export const H3: React.FC<Omit<ResponsiveHeadingProps, 'level'>> = (props) => (
  <ResponsiveHeading level={3} {...props} />
);

export const H4: React.FC<Omit<ResponsiveHeadingProps, 'level'>> = (props) => (
  <ResponsiveHeading level={4} {...props} />
);

export const H5: React.FC<Omit<ResponsiveHeadingProps, 'level'>> = (props) => (
  <ResponsiveHeading level={5} {...props} />
);

export const H6: React.FC<Omit<ResponsiveHeadingProps, 'level'>> = (props) => (
  <ResponsiveHeading level={6} {...props} />
);

